import React, { useState } from 'react';
import styled from 'styled-components';
import { Flex, Modal, Sidebar, VerticalNav, Badge, token, textStyles } from '@bynder/design-system';
import { useTranslate, Translate } from '@bynder/localization';

import { track } from '../../../helpers/track';

enum Tab {
	SEARCH_OPERATORS = 'searchOperators',
	KEYBOARD_SHORTCUTS = 'keyboardShortcuts',
}

const ModalContent = {
	Container: styled.div`
		margin-left: ${token.spacing4};
		margin-bottom: ${token.spacing4};
		width: 100%;
	`,
	H1: styled.h1`
		${textStyles.headingM}
		padding-bottom: ${token.spacing4}
	`,
	H2: styled.h2`
		${textStyles.headingS}
	`,
	P: styled.p`
		${textStyles.paragraphM}
		margin: 0;
	`,
	PGrey: styled.p`
		${textStyles.paragraphM}
		color: ${token.gray500};
	`,
	Strong: styled.strong`
		font-weight: 600;
	`,
	Hr: styled.hr`
		border-top: 1px solid ${token.gray100};
	`,
	BadgeContainer: styled.div`
		display: flex;
		justify-content: space-between;
	`,
};

type Props = {
	isOpen: boolean;
	onClose: () => void;
};

export function HelpModal(props: Props) {
	const [activeTab, setActiveTab] = useState(Tab.SEARCH_OPERATORS);
	const { translate } = useTranslate();

	const activateSearchOperators = () => {
		track("'Search operators' tab is toggled", {
			product_category: 'Spotlight',
			functional_area: 'Search',
		});

		setActiveTab(Tab.SEARCH_OPERATORS);
	};

	const activateKeyboardShortcuts = () => {
		track("'Keyboard shortcuts' tab is toggled", {
			product_category: 'Spotlight',
			functional_area: 'Search',
		});

		setActiveTab(Tab.KEYBOARD_SHORTCUTS);
	};

	return (
		<Modal
			title={translate('spotlightHelp.title')}
			size="fullscreen"
			isOpen={props.isOpen}
			onClose={props.onClose}
		>
			<Flex>
				<Sidebar>
					<VerticalNav>
						<VerticalNav.Item
							as="button"
							onClick={() => activateSearchOperators()}
							isActive={activeTab === Tab.SEARCH_OPERATORS}
						>
							{translate('spotlightHelp.operators.nav')}
						</VerticalNav.Item>
						<VerticalNav.Item
							as="button"
							onClick={() => activateKeyboardShortcuts()}
							isActive={activeTab === Tab.KEYBOARD_SHORTCUTS}
						>
							{translate('spotlightHelp.shortcuts.nav')}
						</VerticalNav.Item>
					</VerticalNav>
				</Sidebar>
				<ModalContent.Container>
					{activeTab === Tab.SEARCH_OPERATORS && (
						<>
							<ModalContent.H1>
								{translate('spotlightHelp.operators.title')}
							</ModalContent.H1>
							<ModalContent.H2>
								{translate('spotlightHelp.operators.and.title')}
							</ModalContent.H2>
							<ModalContent.PGrey>
								<Translate
									id="spotlightHelp.operators.and.example"
									and={<ModalContent.Strong>AND</ModalContent.Strong>}
								/>
							</ModalContent.PGrey>
							<ModalContent.P>
								{translate('spotlightHelp.operators.and.content')}
							</ModalContent.P>
							<ModalContent.Hr />
							<ModalContent.H2>
								{translate('spotlightHelp.operators.or.title')}
							</ModalContent.H2>
							<ModalContent.PGrey>
								<Translate
									id="spotlightHelp.operators.or.example"
									or={<ModalContent.Strong>OR</ModalContent.Strong>}
								/>
							</ModalContent.PGrey>
							<ModalContent.P>
								{translate('spotlightHelp.operators.or.content')}
							</ModalContent.P>
							<ModalContent.Hr />
							<ModalContent.H2>
								{translate('spotlightHelp.operators.not.title')}
							</ModalContent.H2>
							<ModalContent.PGrey>
								<Translate
									id="spotlightHelp.operators.not.example"
									not={<ModalContent.Strong>NOT</ModalContent.Strong>}
								/>
							</ModalContent.PGrey>
							<ModalContent.P>
								{translate('spotlightHelp.operators.not.content')}
							</ModalContent.P>
							<ModalContent.Hr />
							<ModalContent.H2>
								{translate('spotlightHelp.operators.quotes.title')}
							</ModalContent.H2>
							<ModalContent.PGrey>
								{translate('spotlightHelp.operators.quotes.example')}
							</ModalContent.PGrey>
							<ModalContent.P>
								{translate('spotlightHelp.operators.quotes.content')}
							</ModalContent.P>
							<ModalContent.Hr />
							<ModalContent.H2>
								{translate('spotlightHelp.operators.combinations.title')}
							</ModalContent.H2>
							<ModalContent.PGrey>
								<Translate
									id="spotlightHelp.operators.combinations.example"
									andnot={<ModalContent.Strong>AND NOT</ModalContent.Strong>}
								/>
							</ModalContent.PGrey>
							<ModalContent.P>
								{translate('spotlightHelp.operators.combinations.content')}
							</ModalContent.P>
						</>
					)}
					{activeTab === Tab.KEYBOARD_SHORTCUTS && (
						<>
							<ModalContent.H1>
								{translate('spotlightHelp.shortcuts.title')}
							</ModalContent.H1>
							<ModalContent.BadgeContainer>
								<ModalContent.P>
									{translate('spotlightHelp.shortcuts.close.title')}
								</ModalContent.P>
								<Badge color="gray">
									<ModalContent.Strong>
										{translate('spotlightHelp.shortcuts.close.key')}
									</ModalContent.Strong>
								</Badge>
							</ModalContent.BadgeContainer>
							<ModalContent.Hr />
							<ModalContent.BadgeContainer>
								<ModalContent.P>
									{translate('spotlightHelp.shortcuts.down.title')}
								</ModalContent.P>
								<Badge color="gray">
									<ModalContent.Strong>
										{translate('spotlightHelp.shortcuts.down.key')}
									</ModalContent.Strong>
								</Badge>
							</ModalContent.BadgeContainer>
							<ModalContent.Hr />
							<ModalContent.BadgeContainer>
								<ModalContent.P>
									{translate('spotlightHelp.shortcuts.up.title')}
								</ModalContent.P>
								<Badge color="gray">
									<ModalContent.Strong>
										{translate('spotlightHelp.shortcuts.up.key')}
									</ModalContent.Strong>
								</Badge>
							</ModalContent.BadgeContainer>
							<ModalContent.Hr />
							<ModalContent.BadgeContainer>
								<ModalContent.P>
									{translate('spotlightHelp.shortcuts.view.title')}
								</ModalContent.P>
								<Badge color="gray">
									<ModalContent.Strong>
										{translate('spotlightHelp.shortcuts.view.key')}
									</ModalContent.Strong>
								</Badge>
							</ModalContent.BadgeContainer>
						</>
					)}
				</ModalContent.Container>
			</Flex>
		</Modal>
	);
}
